import React, { FC, useCallback, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import {
  FullscreenOutlined,
  FullscreenExitOutlined,
} from "@mui/icons-material";
import {
  FullScreen,
  FullScreenHandle,
  useFullScreenHandle,
} from "react-full-screen";

import StackContent from "@components/StackContent";
import BaseLink from "@components/Link";
import { Colors } from "@src/gatsby-theme-material-ui-top-layout/colors";
import { boolean } from "yup";

interface Props {
  title?: string;
  description?: string;
  ctaUrl?: string;
  ctaLabel?: string;
  image: string;
}

const HowItWorks: FC<Props> = ({
  title = "How it works",
  description,
  ctaLabel = "Learn more",
  ctaUrl,
  image,
}) => {
  const [fullscreen, setFullscreen] = useState<boolean>(false);
  const handle = useFullScreenHandle();
  const reportChange = useCallback(
    (state: boolean, handle: FullScreenHandle) => {
      setFullscreen(state);
    },
    [handle],
  );

  return (
    <StackContent>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        sx={{ position: "relative" }}
      >
        <Typography variant="h2">{title}</Typography>
        {ctaUrl && <BaseLink url={ctaUrl}>{ctaLabel}</BaseLink>}
      </Stack>
      {description && (
        <Typography
          variant="h6"
          mt={2}
          sx={{ color: Colors.TextPrimary, whiteSpace: "pre-wrap" }}
        >
          {description}
        </Typography>
      )}
      <FullScreen handle={handle} onChange={reportChange}>
        <Stack
          alignItems="center"
          justifyContent="center"
          mt={3}
          sx={{
            position: "relative",
            height: "100%",
            backgroundColor: Colors.BlueGrey,
            borderRadius: "15px",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: { xs: "2px", sm: "12px" },
              right: { xs: "4px", sm: "12px" },
              color: Colors.GreyB900,
              cursor: "pointer",
              "&:hover": {
                color: Colors.InfoMain,
              },
            }}
            onClick={fullscreen ? handle.exit : handle.enter}
          >
            {fullscreen ? (
              <FullscreenExitOutlined
                sx={{ fontSize: { xs: "20px", sm: "40px" } }}
              />
            ) : (
              <FullscreenOutlined sx={{ fontSize: "20px" }} />
            )}
          </Box>
          <Box
            component="img"
            src={image}
            sx={{
              width: fullscreen ? { xs: "100vw", sm: "unset" } : "100%",
              maxWidth: fullscreen ? "100%" : "1080px",
              maxHeight: fullscreen ? "100%" : "unset",
            }}
          />
        </Stack>
      </FullScreen>
    </StackContent>
  );
};

export default HowItWorks;
